import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { contactAPI } from '../../services/api';
import '../admin/AdminSections.css';

const ContactSection = () => {
  const [formData, setFormData] = useState({ subject: '', message: '', priority: 'normal' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await contactAPI.getAll();
      setHistory(res.data);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setSent(false);
    try {
      await contactAPI.send(formData);
      setSent(true);
      setFormData({ subject: '', message: '', priority: 'normal' });
      await loadHistory();
      setTimeout(() => setSent(false), 3000);
    } catch (error) {
      alert(error.response?.data?.detail || 'Σφάλμα αποστολής');
    } finally {
      setSending(false);
    }
  };

  const getPriorityIcon = (p) => {
    if (p === 'high') return <AlertTriangle size={14} color="#d32f2f" />;
    if (p === 'low') return <Clock size={14} color="#999" />;
    return <Clock size={14} color="#FF9800" />;
  };

  const getPriorityLabel = (p) => {
    if (p === 'high') return 'Υψηλή';
    if (p === 'low') return 'Χαμηλή';
    return 'Κανονική';
  };

  return (
    <div className="admin-section" data-testid="contact-section">
      <div className="section-header">
        <h2>
          <MessageSquare size={24} />
          Επικοινωνία
        </h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
        {/* Form */}
        <div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '1rem', color: '#1a1a1a' }}>
            Αποστολή Μηνύματος
          </h3>

          <form onSubmit={handleSubmit}>
            <div className="form-group-admin">
              <label>Θέμα *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Θέμα μηνύματος"
                required
                data-testid="contact-subject-input"
              />
            </div>

            <div className="form-group-admin">
              <label>Προτεραιότητα</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                data-testid="contact-priority-select"
              >
                <option value="low">Χαμηλή</option>
                <option value="normal">Κανονική</option>
                <option value="high">Υψηλή</option>
              </select>
            </div>

            <div className="form-group-admin">
              <label>Μήνυμα *</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Γράψτε το μήνυμά σας..."
                rows={5}
                required
                data-testid="contact-message-input"
              />
            </div>

            {sent && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#2e7d32', background: '#e8f5e9', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.9rem' }} data-testid="contact-success-msg">
                <CheckCircle size={18} />
                Το μήνυμα στάλθηκε επιτυχώς!
              </div>
            )}

            <button type="submit" className="btn-primary" disabled={sending} data-testid="contact-submit-btn">
              <Send size={16} />
              {sending ? 'Αποστολή...' : 'Αποστολή'}
            </button>
          </form>
        </div>

        {/* History */}
        <div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '1rem', color: '#1a1a1a' }}>
            Ιστορικό Μηνυμάτων
          </h3>

          {loadingHistory ? (
            <p style={{ color: '#999' }}>Φόρτωση...</p>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
              <MessageSquare size={36} style={{ opacity: 0.4, marginBottom: '0.5rem' }} />
              <p>Δεν υπάρχουν μηνύματα</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: 450, overflowY: 'auto' }}>
              {history.map(msg => (
                <div key={msg.id} style={{
                  padding: '1rem', border: '1px solid #e0e0e0', background: '#fafafa'
                }} data-testid={`contact-msg-${msg.id}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 500, fontSize: '0.95rem', color: '#1a1a1a' }}>{msg.subject}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', color: '#999' }}>
                      {getPriorityIcon(msg.priority)}
                      {getPriorityLabel(msg.priority)}
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#555', lineHeight: 1.5 }}>{msg.message}</p>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#bbb' }}>
                    {new Date(msg.created_at).toLocaleString('el-GR')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactSection;
